<template>
  <Dropdown dusk="filter-selector" :should-close-on-blur="false">
    <Button
      variant="ghost"
      dusk="filter-selector-button"
      icon="funnel"
      trailing-icon="chevron-down"
      padding="tight"
      class="panel-toolbar-filter-trigger"
      :class="{ 'panel-toolbar-filter-trigger--active': filtersAreApplied }"
      :label="activeFilterCount > 0 ? activeFilterCount : ''"
      :aria-label="__('Filter Dropdown')"
    />

    <template #menu>
      <DropdownMenu
        width="350"
        dusk="filter-menu"
        class="panel-row-action-menu panel-filter-menu"
      >
        <ScrollWrap :height="350">
          <div class="panel-row-action-menu-nav panel-filter-menu-nav">
            <div v-if="filtersAreApplied" class="panel-filter-menu-reset">
              <button
                type="button"
                class="panel-filter-menu-reset-btn"
                @click="handleClearSelectedFiltersClick"
              >
                {{ __('Reset Filters') }}
              </button>
            </div>

            <!-- Custom Filters -->
            <div v-for="filter in filters" :key="filter.name">
              <component
                :is="filter.component"
                :filter-key="filter.class"
                :lens="lens"
                :resource-name="resourceName"
                @change="handleFilterChanged"
              />
            </div>

            <!-- Soft Deletes -->
            <FilterContainer v-if="softDeletes" dusk="filter-soft-deletes">
              <span>{{ __('Trashed') }}</span>

              <template #filter>
                <FilterSelectControl
                  :selected="trashedValue"
                  :options="[
                    { value: '', label: '—' },
                    { value: 'with', label: __('With Trashed') },
                    { value: 'only', label: __('Only Trashed') },
                  ]"
                  dusk="trashed-select"
                  size="sm"
                  :include-empty-option="false"
                  @change="trashedValue = $event"
                />
              </template>
            </FilterContainer>

            <!-- Per Page -->
            <FilterContainer v-if="!viaResource" dusk="filter-per-page">
              <span>{{ __('Per Page') }}</span>

              <template #filter>
                <FilterSelectControl
                  :selected="perPageValue"
                  :options="perPageOptionsForFilter"
                  dusk="per-page-select"
                  size="sm"
                  :include-empty-option="false"
                  @change="perPageValue = $event"
                />
              </template>
            </FilterContainer>
          </div>
        </ScrollWrap>
      </DropdownMenu>
    </template>
  </Dropdown>
</template>

<script>
import map from 'lodash/map'
import { Button } from 'laravel-nova-ui'

export default {
  components: { Button },

  emits: [
    'filter-changed',
    'clear-selected-filters',
    'trashed-changed',
    'per-page-changed',
  ],

  props: {
    activeFilterCount: Number,
    filters: Array,
    filtersAreApplied: Boolean,
    lens: { type: String, default: '' },
    perPage: [String, Number],
    perPageOptions: Array,
    resourceName: String,
    softDeletes: Boolean,
    trashed: { type: String, validator: v => ['', 'with', 'only'].includes(v) },
    viaResource: String,
  },

  methods: {
    handleFilterChanged(v) {
      // Older filters generated with our stubs will not have a value, since they committed to the store directly
      // instead of emitting a change event with the `filterKey` and `value`. We need to handle both cases.
      if (v) {
        const { filterClass, value } = v

        if (filterClass) {
          Nova.log(`Updating filter state ${filterClass}: ${value}`)

          this.$store.commit(`${this.resourceName}/updateFilterState`, {
            filterClass,
            value,
          })
        }
      }

      this.$emit('filter-changed')
    },

    handleClearSelectedFiltersClick() {
      Nova.$emit('clear-filter-values')

      setTimeout(() => {
        this.$emit('clear-selected-filters')
      }, 500)
    },
  },

  computed: {
    trashedValue: {
      set(event) {
        let value = event?.target?.value || event

        this.$emit('trashed-changed', value)
      },
      get() {
        return this.trashed
      },
    },

    perPageValue: {
      set(event) {
        let value = event?.target?.value || event

        this.$emit('per-page-changed', value)
      },
      get() {
        return this.perPage
      },
    },

    /**
     * Return the values for the per page filter
     */
    perPageOptionsForFilter() {
      return map(this.perPageOptions, option => {
        return { value: option, label: option }
      })
    },
  },
}
</script>
